import { Link } from 'react-router-dom';
import { PriceRow, BundleRows, PRICE_GROUP } from './PricingRows';
import RetrogradeRenameTool from './RetrogradeRenameTool';
import QrWallpaperModal from '@/components/qr/QrWallpaperModal';
import { useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { supabase } from '@/lib/supabase';
import { ArrowLeftIcon, PhotoIcon, CurrencyDollarIcon, ArrowDownTrayIcon, ExclamationCircleIcon, CheckCircleIcon, ArrowPathIcon, ChartBarIcon, PlusIcon, LockClosedIcon, LockOpenIcon, TrashIcon, GlobeAltIcon, ArrowUpTrayIcon, XMarkIcon, CloudIcon, CircleStackIcon, PencilIcon, EnvelopeIcon, TagIcon, QrCodeIcon, CheckIcon, StarIcon } from '@heroicons/react/24/outline';
import { Eye, EyeOff, Radio } from 'lucide-react';
import AdminTagsPanel from './AdminTagsPanel';
import AdminDownloadsPanel from './AdminDownloadsPanel';
import AdminPhotosBrowse from './AdminPhotosBrowse';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { sendToGalleryResilient } from '../live/sendToGallery';
import { useToast } from '@/components/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { ExpandableScreen, ExpandableScreenContent } from '@/components/ui/expandable-screen';
import { GalleryCountdownOverlay, shouldShowCountdown } from '@/components/ui/gallery-countdown-overlay';
import { LoadingSpinner } from '@/components/Loading';
import { isAdminUser } from '@/utils/admin';

// Lazy: the screen generator bundles its own typeface; nobody else should download it.
const HalftoneModal = lazy(() => import('@/components/halftone/HalftoneModal'));

interface AdminGalleryViewProps {
    onBack: () => void;
    isPhotographerView?: boolean;
    /** The admin console draws its own panel title above this view; skip ours so it is not shown twice. */
    hideTitle?: boolean;
}

interface GalleryStats {
    totalOrders: number;
    totalRevenue: number;
    bookingRevenue?: number;
    recentOrders: any[];
    recentPhotos: any[];
}

interface PhotoLibrary {
    id: string;
    name: string;
    slug: string;
    description: string;
    is_private: boolean;
    published?: boolean;
    google_drive_folder_id?: string;
    photo_count?: number;
    cover_image_url?: string;
    price?: number;
    commercial_included?: boolean;
    owner_id?: string;
    invited_emails?: string; // Comma-separated list of invited client emails
    buyer_emails?: string | null; // À la carte: see the album, buy photos at buyer_price each
    buyer_price?: number | null;
    buyer_bundles?: { photo_count: number; price: number }[] | null;
    buyer_album_price?: number | null;
    album_price?: number | null; // Whole album, for every buyer
    user_id?: string;
    live_mode?: boolean; // A live event is running — see /admin/live
}

interface PricingOption {
    id?: string;
    library_id?: string;
    name: string;
    price: number;
    photo_count: number; // 1 = single, -1 = all, >1 = bundle
    is_active?: boolean;
}

type GalleryPhoto = { id: string; title: string; google_drive_file_id: string; mime_type: string | null };

const photoSrc = (photo: GalleryPhoto, px: number) =>
    `/api/gallery/stream?fileId=${encodeURIComponent(photo.google_drive_file_id)}&size=${px}`;

/**
 * One square thumbnail of a gallery photo; tap calls onTap (select it, or open
 * the full photo list). Never a link to the raw image: that left the dashboard
 * for a bare image on a white page with no way back (owner, 2026-11-02).
 * Square by padding, not aspect-ratio (iOS Safari squeezed aspect-ratio tiles
 * in a grid). A video gets a plain tile: the stream route sends a video whole.
 */
function GalleryThumb({ photo, size = 200, onTap, selected = false, isCover = false }: {
    photo: GalleryPhoto;
    size?: number;
    onTap: () => void;
    selected?: boolean;
    isCover?: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onTap}
            aria-pressed={selected}
            className="relative block w-full h-0 pb-[100%] bg-white/5 overflow-hidden"
            title={photo.title}
        >
            {photo.mime_type?.startsWith('video/') ? (
                <span className="absolute inset-0 flex items-center justify-center text-white/60 text-[9px] font-bold uppercase">Video</span>
            ) : (
                <img src={photoSrc(photo, size)} alt={photo.title} loading="lazy" className={`absolute inset-0 w-full h-full object-cover transition-opacity ${selected ? 'opacity-50' : ''}`} />
            )}
            {isCover && (
                <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/70 text-white text-[8px] font-black uppercase tracking-widest">Cover</span>
            )}
            {selected && (
                <span className="absolute top-1 right-1 w-6 h-6 bg-white text-black flex items-center justify-center rounded-full">
                    <CheckIcon className="w-4 h-4" />
                </span>
            )}
        </button>
    );
}

/** A phone photo (HEIC/JPEG/PNG, any size) as a JPEG no wider or taller than max px. */
async function shrinkToJpeg(file: File, max: number): Promise<Blob> {
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = () => reject(new Error('This photo could not be read. Try a JPEG or PNG.'));
            i.src = url;
        });
        const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.naturalWidth * scale);
        canvas.height = Math.round(img.naturalHeight * scale);
        canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise<Blob | null>(r => canvas.toBlob(r, 'image/jpeg', 0.85));
        if (!blob) throw new Error('This photo could not be converted.');
        return blob;
    } finally {
        URL.revokeObjectURL(url);
    }
}

export default function AdminGalleryView({ onBack, isPhotographerView = false, hideTitle = false }: AdminGalleryViewProps) {
    const { user } = useAuth(); // Get authenticated user
    const [stats, setStats] = useState<GalleryStats | null>(null);
    const [libraries, setLibraries] = useState<PhotoLibrary[]>([]);
    // Which library's rename panel is open. Per-library, so a preview can
    // never be shown for one gallery and applied to another.
    const [renameFor, setRenameFor] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [verifying, setVerifying] = useState(false);
    const [healthStatus, setHealthStatus] = useState<'unknown' | 'healthy' | 'issues'>('unknown');

    // Create Gallery State
    // Create/Edit Gallery State
    const [isManaged, setIsManaged] = useState(false); // Modal open state
    const [editingId, setEditingId] = useState<string | null>(null); // If set, we are editing
    // Photo IDs staged from the batch-editing grid, waiting to land in whatever
    // gallery the admin creates next — see openCreateModalForPhotos / handleSaveGallery.
    const [pendingGalleryPhotoIds, setPendingGalleryPhotoIds] = useState<string[]>([]);
    const [uploadMode, setUploadMode] = useState<'drive' | 'upload'>('drive');
    const [filesData, setFilesData] = useState<{ file: File; thumbnail?: Blob; previewUrl: string }[]>([]);
    // While an upload runs, photos picked from the file box join its queue
    // instead of waiting for (and being cleared by) the end of it (owner,
    // 2026-11-02: 33 photos added during a 153-photo upload were dropped).
    const liveUploadRef = useRef<((items: { file: File; thumbnail?: Blob; previewUrl: string }[]) => void) | null>(null);
    const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadCounts, setUploadCounts] = useState<{ done: number; failed: number; total: number; lastError?: string } | null>(null);
    const [saving, setSaving] = useState(false);
    // Per-file upload state, so the list shows which photos are live and which
    // failed — the gallery renames them, so the count alone can't tell you.
    type FileStatus = { state: 'waiting' | 'sending' | 'done' | 'failed'; progress: number; error?: string };
    const [fileStatus, setFileStatus] = useState<Map<File, FileStatus>>(new Map());
    // Photos already in the gallery being edited, shown on the edit screen.
    const [showAllPhotos, setShowAllPhotos] = useState(false);
    const [galleryPhotos, setGalleryPhotos] = useState<GalleryPhoto[]>([]);
    const loadGalleryPhotos = async (libraryId: string) => {
        const { data } = await supabase
            .from('photos')
            .select('id, title, google_drive_file_id, mime_type')
            .eq('library_id', libraryId)
            .order('updated_at', { ascending: false })
            .limit(500);
        setGalleryPhotos(data || []);
    };
    // Tapping a photo on the all-photos screen selects it; the bar under the
    // grid then views it, makes it the cover, or deletes the selection.
    const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(new Set());
    const [viewingPhoto, setViewingPhoto] = useState<GalleryPhoto | null>(null);
    const [photoAction, setPhotoAction] = useState<'cover' | 'delete' | null>(null);
    useEffect(() => {
        if (isManaged && editingId) loadGalleryPhotos(editingId);
        else setGalleryPhotos([]);
        if (!isManaged) setShowAllPhotos(false);
    }, [isManaged, editingId]);
    useEffect(() => {
        if (!showAllPhotos) { setSelectedPhotoIds(new Set()); setViewingPhoto(null); }
    }, [showAllPhotos]);
    const togglePhoto = (id: string) => setSelectedPhotoIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const markFile = (file: File, status: FileStatus) =>
        setFileStatus(prev => { const next = new Map(prev); next.set(file, status); return next; });
    // Which gallery is pulling new files in from its Drive folder right now.
    const [syncingSlug, setSyncingSlug] = useState<string | null>(null);
    // Photographers have no tab bar, so they stay on whatever tab this starts
    // on. Starting them on 'photos' rendered the photo browser instead of their
    // own galleries, and they had no way to switch.
    const [activeTab, setActiveTab] = useState<'galleries' | 'applications' | 'photos' | 'tags' | 'downloads'>(isPhotographerView ? 'galleries' : 'photos');
    const [applications, setApplications] = useState<any[]>([]);
    const [appsLoading, setAppsLoading] = useState(false);
    const [pendingAppsCount, setPendingAppsCount] = useState(0);

    const [modalData, setModalData] = useState<Partial<PhotoLibrary>>({
        is_private: false,
        price: 5.00,
        commercial_included: false,
    });
    const [pricingOptions, setPricingOptions] = useState<PricingOption[]>([]);
    const [deletedPricingIds, setDeletedPricingIds] = useState<string[]>([]);
    // Price per photo: the gallery's 1-photo pricing option (what checkout and
    // the gallery page read first), kept in step with photo_libraries.price.
    const singleOptionIdx = pricingOptions.findIndex(o => o.photo_count === 1);
    const singlePriceValue = singleOptionIdx >= 0 ? pricingOptions[singleOptionIdx].price : (modalData.price ?? 5);
    const setSinglePrice = (price: number) => {
        if (singleOptionIdx >= 0) {
            setPricingOptions(pricingOptions.map((o, i) => i === singleOptionIdx ? { ...o, price } : o));
        } else {
            setPricingOptions([{ id: '', name: 'Single Photo', photo_count: 1, price }, ...pricingOptions]);
        }
        if (price > 0) setModalData({ ...modalData, price });
    };
    const [invitedEmails, setInvitedEmails] = useState<string[]>([]); // Persistent list of invited emails
    // À la carte (owner, 2026-11-02): same album, but these people buy the
    // photos they want at buyerPrice each instead of getting everything free.
    const [buyerEmails, setBuyerEmails] = useState<string[]>([]);
    const [originalBuyerEmails, setOriginalBuyerEmails] = useState<string[]>([]);
    const [newBuyerInput, setNewBuyerInput] = useState('');
    const [buyerPrice, setBuyerPrice] = useState<number>(5);
    // À la carte deals: "N photos for $X", and the whole album for one price.
    const [buyerBundles, setBuyerBundles] = useState<{ photo_count: number; price: number }[]>([]);
    const [buyerAlbumPrice, setBuyerAlbumPrice] = useState<number | ''>('');
    // Whole-album price for everyone who buys from this gallery (owner, 2026-11-02).
    const [albumPrice, setAlbumPrice] = useState<number | ''>('');
    const [originalInvitedEmails, setOriginalInvitedEmails] = useState<string[]>([]); // Track original emails to detect new ones
    const [newEmailInput, setNewEmailInput] = useState(''); // Input for adding new emails

    // Photographer Invite State
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [qrLibrary, setQrLibrary] = useState<{ slug: string; name: string } | null>(null);
    const [isHalftoneOpen, setIsHalftoneOpen] = useState(false);
    const [inviteData, setInviteData] = useState({ email: '', name: '' });
    const [inviteLoading, setInviteLoading] = useState(false);

    // Countdown overlay state for "LAST NIGHT" gallery
    const [countdownExpired, setCountdownExpired] = useState(false);
    const targetTime8PM = useMemo(() => {
        const target = new Date();
        target.setHours(11, 11, 0, 0); // 11:11 AM today
        return target;
    }, []);

    const { success, error, info } = useToast();

    useEffect(() => {
        if (user) {
            loadGalleryStats();
            loadApplications();
        }
    }, [user]);

    const loadApplications = async () => {
        try {
            setAppsLoading(true);
            const { data, error } = await supabase
                .from('photographer_applications')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setApplications(data || []);
            setPendingAppsCount(data?.filter(a => a.status === 'pending').length || 0);
        } catch (err) {
            console.error('Error loading applications:', err);
        } finally {
            setAppsLoading(false);
        }
    };

    const loadGalleryStats = async () => {
        try {
            setLoading(true);

            // 1. Get libraries (filtered by owner if photographer mode)
            let query = supabase
                .from('photo_libraries')
                .select('*')
                .order('created_at', { ascending: false });

            if (isPhotographerView && user && !isAdminUser(user)) {
                query = query.eq('owner_id', user.id);
            }

            const { data: libs, error: libError } = await query;

            if (libError) throw libError;

            // Count photos per library with explicit queries (reliable regardless of FK schema)
            const countsByLib: Record<string, number> = {};
            if (libs && libs.length > 0) {
                const countResults = await Promise.all(
                    libs.map((lib: any) =>
                        supabase
                            .from('photos')
                            .select('id', { count: 'exact' })
                            .eq('library_id', lib.id)
                            .then(({ count }) => ({ id: lib.id, count: count || 0 }))
                    )
                );
                countResults.forEach(({ id, count }: { id: string; count: number }) => {
                    countsByLib[id] = count;
                });
            }

            const processedLibs = (libs || []).map((lib: any) => ({
                ...lib,
                photo_count: countsByLib[lib.id] || 0
            }));

            setLibraries(processedLibs);

            // 2. Get orders (related to these libraries)
            // If photographer, we only want orders for THEIR libraries
            // We can fetch orders where library_id is in the list of fetched libraries
            const libraryIds = processedLibs.map(l => l.id);

            let ordersQuery = supabase
                .from('photo_orders')
                .select('*')
                .order('created_at', { ascending: false });

            // If we have libraries, filter orders by them. 
            // If no libraries, no orders relevant for this view (or user has 0 galleries)
            if (isPhotographerView) {
                if (libraryIds.length > 0) {
                    // Note: photo_orders usually has items JSON, not direct library_id column unless added.
                    // Checking schema... typically order items link to photos/libraries. 
                    // Assuming 'photo_orders' doesn't strictly link to library_id at root. 
                    // BUT for stats, we might fallback to general revenue if schema is complex.
                    // Let's assume for now we just show 0 if specialized filtering is too complex without a join table.
                    // Actually, let's skip order filtering complexity for MVP and just show 0 or "contact admin" for revenue if strict.
                    // OR, if 'photo_orders' has 'items' which contains 'library_id'.
                    // For now, let's safely NOT show all platform orders to a photographer.
                    // We'll fetch orders but maybe filter client-side if needed, or better:
                    // ONLY fetch if we can link it.
                    // Use a simple heuristic: if photographer view, don't show platform-wide stats.
                }
            }

            const { data: orders, error: supabaseError } = await ordersQuery;

            // Filter orders for photographer view (simple client-side check if possible, or just hide stats UI)
            // Since we can't easily join on JSONB items without complex query, 
            // let's just use the fetched orders for GLOBAL admin, and EMPTY/Filtered for photographer.

            // Filter out Sandbox data
            const isTestEmail = (email: string | null | undefined) => {
                if (!email) return false;
                const testPatterns = ['test', 'demo', 'admin', 'dev', 'staging', 'dummy'];
                return testPatterns.some(pattern => email.toLowerCase().includes(pattern));
            };

            let validOrders = (orders || []).filter(o =>
                o.payment_status === 'completed' &&
                !isTestEmail(o.email) &&
                (o.metadata as any)?.environment !== 'sandbox'
            );
            if (isPhotographerView) {
                // If we can't filter easily, show empty to avoid leaking other's data
                // TODO: Implement proper order-to-owner tracking
                validOrders = [];
            }

            // 3. Get Recent Photos (Sorted by capture date to show most recently taken photos)
            // Fetch a pool of recent candidates, then sort client-side by metadata.date_taken
            const { data: recentPhotosRaw, error: photosError } = await supabase
                .from('photos')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(100);
            const recentPhotos = (recentPhotosRaw || [])
                .sort((a, b) => {
                    const aDate = a.metadata?.date_taken || a.created_at || '';
                    const bDate = b.metadata?.date_taken || b.created_at || '';
                    return bDate.localeCompare(aDate);
                })
                .slice(0, 3);

            if (photosError) console.error('Error fetching recent photos:', photosError);

            // Calculate photo-order revenue
            const photoRevenue = validOrders.reduce((sum, order) => sum + (order.total_amount_cents || 0), 0) / 100 || 0;

            // 4. Get paid booking invoices (CRM revenue)
            let bookingRevenue = 0;
            try {
                const { data: paidInvoices } = await supabase
                    .from('invoices')
                    .select('total')
                    .eq('status', 'paid');
                bookingRevenue = (paidInvoices || []).reduce((sum, inv) => sum + (Number(inv.total) || 0), 0);
            } catch (invoiceErr) {
                console.warn('[loadGalleryStats] Could not load invoice revenue:', invoiceErr);
            }

            // Gallery cards show photo (gallery-order) revenue ONLY.
            // Booking revenue is surfaced in its own dedicated card so the two streams
            // are never conflated.
            setStats({
                totalOrders: validOrders.length || 0,
                totalRevenue: photoRevenue,
                bookingRevenue,
                recentOrders: validOrders.slice(0, 10) || [],
                recentPhotos: recentPhotos || []
            });
        } catch (err) {
            console.error('Error loading gallery stats:', err);
            error('Failed to load gallery data');
        } finally {
            setLoading(false);
        }
    };

    // Absolute, so the cover also shows wherever the URL travels (affiliate
    // product cards read cover_image_url as-is).
    const coverUrlFor = (photo: GalleryPhoto) => `https://www.thelostandunfounds.com${photoSrc(photo, 1600)}`;
    const isCoverPhoto = (photo: GalleryPhoto) =>
        !!modalData.cover_image_url && modalData.cover_image_url.includes(`fileId=${encodeURIComponent(photo.google_drive_file_id)}&`);

    /** Saves the cover straight away, so it sticks even if the form is closed without SAVE. */
    const saveCover = async (url: string | null) => {
        if (!editingId) throw new Error('Save the gallery first.');
        const { data, error: coverError } = await supabase
            .from('photo_libraries')
            .update({ cover_image_url: url })
            .eq('id', editingId)
            .select('id');
        if (coverError) throw coverError;
        // RLS refuses an update by matching no rows, not with an error.
        if (!data?.length) throw new Error('You can only change the cover of your own gallery.');
        setModalData(prev => ({ ...prev, cover_image_url: url ?? '' }));
        setLibraries(prev => prev.map(l => l.id === editingId ? { ...l, cover_image_url: url ?? undefined } : l));
    };

    const makeSelectedCover = async () => {
        const photo = galleryPhotos.find(p => selectedPhotoIds.has(p.id));
        if (!photo || selectedPhotoIds.size !== 1 || photoAction) return;
        setPhotoAction('cover');
        try {
            await saveCover(coverUrlFor(photo));
            setSelectedPhotoIds(new Set());
            success('Album cover set');
        } catch (err: any) {
            error(err.message || 'Could not set the cover');
        } finally {
            setPhotoAction(null);
        }
    };

    const deleteSelectedPhotos = async () => {
        const ids = [...selectedPhotoIds];
        if (!ids.length || photoAction) return;
        if (!confirm(`Delete ${ids.length} photo${ids.length === 1 ? '' : 's'} from this gallery and from Google Drive? This cannot be undone.`)) return;
        setPhotoAction('delete');
        try {
            const deleted = galleryPhotos.filter(p => selectedPhotoIds.has(p.id));
            const { data: { session: authSession } } = await supabase.auth.getSession();
            const res = await fetch('/api/gallery/batch-delete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authSession?.access_token || ''}` },
                body: JSON.stringify({ photoIds: ids }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.error || 'Could not delete the photos');
            setGalleryPhotos(prev => prev.filter(p => !selectedPhotoIds.has(p.id)));
            setLibraries(prev => prev.map(l => l.id === editingId ? { ...l, photo_count: Math.max(0, (l.photo_count || 0) - ids.length) } : l));
            setSelectedPhotoIds(new Set());
            setViewingPhoto(null);
            // A deleted cover would show a broken image: clear it.
            if (deleted.some(isCoverPhoto)) await saveCover(null).catch(() => {});
            if (data.driveErrors?.length) {
                error(`Deleted from the gallery, but ${data.driveErrors.length} file(s) could not be removed from Google Drive.`);
            } else {
                success(`Deleted ${ids.length} photo${ids.length === 1 ? '' : 's'}`);
            }
        } catch (err: any) {
            error(err.message || 'Could not delete the photos');
        } finally {
            setPhotoAction(null);
        }
    };

    const handleSaveGallery = async (e: React.FormEvent) => {
        e.preventDefault();
        if (saving) return;
        setSaving(true);
        setLoading(true);
        try {
            let finalLibrary: PhotoLibrary | null = null;

            const libraryData = {
                name: modalData.name,
                slug: modalData.slug || (modalData.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || ''),
                description: modalData.description || '',
                is_private: modalData.is_private || false,
                published: modalData.published ?? true,
                commercial_included: modalData.commercial_included || false,
                google_drive_folder_id: uploadMode === 'drive' ? (modalData.google_drive_folder_id || null) : null,
                cover_image_url: modalData.cover_image_url || null,
                price: modalData.price || 5.00,
                // Lowercased: Supabase Auth stores sign-in emails lowercased, and the
                // photos RLS policy matches them against this list. A capitalised
                // entry ("Jenna.taylor@...") let the client in to an empty gallery.
                invited_emails: invitedEmails.map(e => e.trim().toLowerCase()).join(','),
                buyer_emails: buyerEmails.map(e => e.trim().toLowerCase()).join(',') || null,
                buyer_price: buyerPrice > 0 ? buyerPrice : 5,
                buyer_bundles: buyerBundles.filter(b => b.photo_count > 1 && b.price > 0),
                buyer_album_price: typeof buyerAlbumPrice === 'number' && buyerAlbumPrice > 0 ? buyerAlbumPrice : null,
                album_price: typeof albumPrice === 'number' && albumPrice > 0 ? albumPrice : null,
                // Assign owner to current user for all new galleries
                ...(user && !editingId ? { user_id: user.id, owner_id: user.id } : {})
            };

            if (editingId) {
                // Update Existing
                const { data, error: updateError } = await supabase
                    .from('photo_libraries')
                    .update(libraryData)
                    .eq('id', editingId)
                    .select()
                    .single();

                if (updateError) throw updateError;
                finalLibrary = data;
            } else {
                // Create New
                const { data, error: createError } = await supabase
                    .from('photo_libraries')
                    .insert([libraryData])
                    .select()
                    .single();

                if (createError) throw createError;
                finalLibrary = data;
            }

            if (!finalLibrary) throw new Error('Failed to save library');

            // 2. Pricing options — saved before the uploads, so a long upload that
            // gets interrupted never loses the prices
            const libraryId = finalLibrary.id;

            // Handle Deletions
            if (deletedPricingIds.length > 0) {
                await supabase
                    .from('gallery_pricing_options')
                    .delete()
                    .in('id', deletedPricingIds);
            }

            // Handle Upserts
            for (const option of pricingOptions) {
                // Options with no photos (the old "-1 = full gallery") never priced anything.
                if (!(option.photo_count > 0)) {
                    if (option.id) await supabase.from('gallery_pricing_options').update({ is_active: false }).eq('id', option.id);
                    continue;
                }
                const payload = {
                    library_id: libraryId,
                    name: option.name?.trim() || (option.photo_count === 1 ? 'Single Photo' : `${option.photo_count} Photos`),
                    price: option.price,
                    photo_count: option.photo_count,
                    is_active: true
                };

                if (option.id) {
                    await supabase
                        .from('gallery_pricing_options')
                        .update(payload)
                        .eq('id', option.id);
                } else {
                    await supabase
                        .from('gallery_pricing_options')
                        .insert(payload);
                }
            }

            // 3. Handle File Uploads (Only if new files added)
            // Note: If editing, this ADDS to the library. It doesn't replace.
            // Photos go to the gallery's Google Drive folder through the live
            // ingest pipeline (photos.google_drive_file_id is NOT NULL, so a
            // Supabase Storage upload could never become a photo row). A gallery
            // made without a Drive link gets its own folder on the first upload.
            let failedUploads: typeof filesData = [];
            let uploadTotal = 0;
            const queue: typeof filesData = [];
            let drain = async () => {};
            if (uploadMode === 'upload' && filesData.length > 0 && finalLibrary) {
                queue.push(...filesData);
                let total = queue.length;
                const relay = { only: false };
                const fractions = new Map<typeof filesData[number], number>();
                const report = () => {
                    let sum = 0;
                    fractions.forEach(f => { sum += f; });
                    setUploadProgress((sum / total) * 100);
                };
                setUploadProgress(0);
                setUploadCounts({ done: 0, failed: 0, total });
                setFileStatus(new Map(queue.map(item => [item.file, { state: 'waiting', progress: 0 } as FileStatus])));
                const slug = finalLibrary.slug;
                const worker = async () => {
                    while (queue.length) {
                        const item = queue.shift()!;
                        markFile(item.file, { state: 'sending', progress: 0 });
                        try {
                            await sendToGalleryResilient({
                                file: item.file,
                                slug,
                                relay,
                                onProgress: f => { fractions.set(item, f); report(); markFile(item.file, { state: 'sending', progress: f }); },
                            });
                            fractions.set(item, 1);
                            markFile(item.file, { state: 'done', progress: 1 });
                            if (editingId) void loadGalleryPhotos(finalLibrary.id);
                            setUploadCounts(c => c && { ...c, done: c.done + 1 });
                        } catch (uploadErr: any) {
                            console.error(`Failed to upload ${item.file.name}`, uploadErr);
                            fractions.set(item, 1);
                            markFile(item.file, { state: 'failed', progress: 0, error: uploadErr?.message });
                            failedUploads.push(item);
                            setUploadCounts(c => c && { ...c, failed: c.failed + 1, lastError: uploadErr?.message });
                        }
                        report();
                    }
                };
                // Photos added mid-upload join this queue (see handleFileSelect).
                liveUploadRef.current = (items) => {
                    queue.push(...items);
                    total += items.length;
                    setUploadCounts(c => c && { ...c, total: c.total + items.length });
                    items.forEach(i => markFile(i.file, { state: 'waiting', progress: 0 }));
                    report();
                };
                // Two at a time, until the queue is empty — including photos
                // that arrived while the last ones were finishing.
                drain = async () => {
                    while (queue.length) await Promise.all(Array.from({ length: Math.min(2, queue.length) }, worker));
                    uploadTotal = total;
                };
                await drain();
            }

            const uploaded = uploadMode === 'upload' ? uploadTotal - failedUploads.length : 0;
            if (failedUploads.length > 0) {
                error(`${failedUploads.length} of ${uploadTotal} photos did not upload. Tap SAVE GALLERY to retry them.`);
            } else {
                success(
                    (editingId ? 'Gallery updated' : 'Gallery created')
                    + (uploaded > 0 ? ` — ${uploaded} photo${uploaded === 1 ? '' : 's'} uploaded` : ' successfully'),
                );
            }

            // CRITICAL: Update local state immediately to avoid stale data if user re-opens modal before fetch completes
            if (finalLibrary) {
                console.log('[handleSaveGallery] Updating local state for:', finalLibrary.name, 'invited:', finalLibrary.invited_emails);
                setLibraries(prev => {
                    if (editingId) {
                        return prev.map(l => l.id === finalLibrary!.id ? { ...l, ...finalLibrary, photo_count: l.photo_count } : l);
                    } else {
                        return [{ ...finalLibrary!, photo_count: 0 }, ...prev];
                    }
                });
            }

            loadGalleryStats();

            // 6. Handle Invitations - Only send for NEW emails
            console.log('[handleSaveGallery] Checking invites. Current:', invitedEmails, 'Original:', originalInvitedEmails);
            // New album owners and new à la carte buyers alike; the invite route
            // sends each the copy for their list.
            const newEmails = [
                ...invitedEmails.filter(e => !originalInvitedEmails.includes(e)),
                ...buyerEmails.filter(e => !originalBuyerEmails.includes(e) && !invitedEmails.includes(e)),
            ];
            console.log('[handleSaveGallery] New emails calculated:', newEmails);
            if (newEmails.length > 0 && finalLibrary) {
                try {
                    await fetch('/api/gallery/invite', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            libraryId: finalLibrary.id,
                            emails: newEmails
                        })
                    });
                    success(`Invitations sent to ${newEmails.length} new client(s)`);
                } catch (inviteErr) {
                    console.error('Failed to send invitations:', inviteErr);
                    error('Gallery saved, but failed to send invitation emails');
                }
            }
            // 7. If photos were staged from the batch-editing grid (Save → Create Gallery),
            // move them into the gallery that was just created.
            if (!editingId && finalLibrary && pendingGalleryPhotoIds.length > 0) {
                try {
                    const { data: { session: authSession } } = await supabase.auth.getSession();
                    const res = await fetch('/api/gallery/batch-move', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authSession?.access_token || ''}` },
                        body: JSON.stringify({ photoIds: pendingGalleryPhotoIds, targetLibraryId: finalLibrary.id }),
                    });
                    if (res.ok) {
                        success(`${pendingGalleryPhotoIds.length} photo(s) moved into "${finalLibrary.name}"`);
                    } else {
                        error('Gallery created, but photos could not be moved into it');
                    }
                } catch (moveErr) {
                    console.error('Failed to move staged photos into new gallery:', moveErr);
                    error('Gallery created, but photos could not be moved into it');
                } finally {
                    setPendingGalleryPhotoIds([]);
                }
            }

            // Anything picked while the gallery was finishing up goes too.
            await drain();
            liveUploadRef.current = null;

            if (failedUploads.length > 0) {
                // Stay open on the saved gallery with only the photos that failed,
                // so SAVE GALLERY retries them instead of creating a second gallery.
                setEditingId(finalLibrary.id);
                setModalData(finalLibrary);
                setOriginalInvitedEmails(invitedEmails);
                setFilesData(failedUploads);
                setDeletedPricingIds([]);
                return;
            }

            setFilesData([]);
            setUploadCounts(null);
            setFileStatus(new Map());
            setUploadProgress(0);
            setInvitedEmails([]);
            setOriginalInvitedEmails([]);
            setNewEmailInput('');
            setBuyerEmails([]);
            setOriginalBuyerEmails([]);
            setNewBuyerInput('');
            setIsManaged(false);
        } catch (err: any) {
            console.error('Error saving gallery:', err);
            error(err.message || 'Failed to save gallery');
        } finally {
            liveUploadRef.current = null;
            setLoading(false);
            setSaving(false);
        }
    };

    // Pull in photos added straight to the gallery's Drive folder — e.g. from the
    // Google Drive app, which keeps uploading in the background on a phone,
    // where a web page cannot. The scheduled sync only runs weekly.
    const handleSyncFromDrive = async (lib: PhotoLibrary) => {
        if (syncingSlug) return;
        setSyncingSlug(lib.slug);
        try {
            const res = await fetch('/api/admin/sync-library', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ slug: lib.slug }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `Sync failed (${res.status})`);
            await loadGalleryStats();
            success(`"${lib.name}" synced from Drive — ${data.synced ?? 0} files checked${typeof data.deleted === 'number' && data.deleted > 0 ? `, ${data.deleted} removed` : ''}.`);
        } catch (err: any) {
            error(err.message || 'Sync from Drive failed');
        } finally {
            setSyncingSlug(null);
        }
    };

    const handleResendAllInvites = async () => {
        if (!editingId || invitedEmails.length === 0) return;
        if (!confirm(`Resend access emails to all ${invitedEmails.length} invited client(s)?`)) return;
        try {
            const res = await fetch('/api/gallery/invite', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ libraryId: editingId, emails: invitedEmails })
            });
            const data = await res.json();
            if (data.succeeded?.length > 0) {
                success(`Access emails resent to ${data.succeeded.length} client(s)`);
            } else {
                error('Failed to resend access emails');
            }
        } catch (err: any) {
            error('Failed to resend access emails');
        }
    };

    const generateVideoThumbnail = (file: File): Promise<Blob | null> => {
        return new Promise((resolve) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.src = URL.createObjectURL(file);
            video.muted = true;
            video.playsInline = true;
            video.currentTime = 1; // Capture at 1s

            video.onloadeddata = () => {
                // Ensure we have data
                if (video.readyState >= 2) {
                    video.currentTime = 1;
                }
            };

            video.onseeked = () => {
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
                canvas.toBlob((blob) => {
                    URL.revokeObjectURL(video.src);
                    resolve(blob);
                }, 'image/jpeg', 0.8);
            };

            video.onerror = () => {
                URL.revokeObjectURL(video.src);
                resolve(null);
            };
        });
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            // Photos and videos only: the Drive ingest pipeline refuses anything else.
            const selectedFiles = Array.from(e.target.files).filter(f => f.type.startsWith('image/') || f.type.startsWith('video/') || f.type === '');
            const processedFiles = await Promise.all(selectedFiles.map(async (file) => {
                let thumbnail: Blob | undefined;
                let previewUrl = URL.createObjectURL(file);

                if (file.type.startsWith('video/')) {
                    const thumbBlob = await generateVideoThumbnail(file);
                    if (thumbBlob) {
                        thumbnail = thumbBlob;
                        previewUrl = URL.createObjectURL(thumbBlob);
                    }
                }

                return { file, thumbnail, previewUrl };
            }));

            setFilesData(prev => [...prev, ...processedFiles]);
            // Mid-upload: send them with the running upload.
            liveUploadRef.current?.(processedFiles);
            e.target.value = '';
        }
    };

    const removeFile = (index: number) => {
        setFilesData(prev => {
            const newFiles = [...prev];
            URL.revokeObjectURL(newFiles[index].previewUrl); // Cleanup
            newFiles.splice(index, 1);
            return newFiles;
        });
    };

    const handleApplicationStatus = async (id: string, newStatus: 'approved' | 'rejected') => {
        try {
            const { error: updateError } = await supabase
                .from('photographer_applications')
                .update({ status: newStatus })
                .eq('id', id);

            if (updateError) throw updateError;

            success(`Application ${newStatus}`);
            loadApplications();

            // If approved, we might want to automatically invite them or notify them
            if (newStatus === 'approved') {
                const app = applications.find(a => a.id === id);
                if (app) {
                    // Logic to send invitation email could go here
                    // for now just log it
                    console.log(`Application approved for ${app.email}. Ready for invitation.`);
                }
            }
        } catch (err) {
            error('Failed to update application status');
        }
    };

    const handleDeleteGallery = async (id: string, name: string) => {
        if (!confirm(`Are you sure you want to delete gallery "${name}"? This cannot be undone.`)) return;

        try {
            const { error: delError } = await supabase
                .from('photo_libraries')
                .delete()
                .eq('id', id);

            if (delError) throw delError;

            success('Gallery deleted');
            loadGalleryStats();
        } catch (err) {
            error('Failed to delete gallery');
        }
    };

    const openEditModal = async (lib: PhotoLibrary) => {
        setEditingId(lib.id);
        setModalData({
            ...lib,
            price: lib.price ?? 5.00,
            commercial_included: lib.commercial_included ?? false,
        });
        setFilesData([]);
        setUploadCounts(null);
        setFileStatus(new Map());
        setUploadProgress(0);
        setCoverImageFile(null);
        setUploadMode(lib.google_drive_folder_id ? 'drive' : 'upload');

        // Fetch pricing options
        const { data: pricing } = await supabase
            .from('gallery_pricing_options')
            .select('*')
            .eq('library_id', lib.id)
            .order('photo_count', { ascending: true }); // -1 (All) usually comes first if sorted numerically, wait. -1 is small. We want logic sort later.

        if (pricing && pricing.length > 0) {
            setPricingOptions(pricing);
        } else {
            // Determine implicit defaults if none exist
            setPricingOptions([
                { name: 'Single Photo', price: lib.price || 5.00, photo_count: 1 }
            ]);
        }
        // Load existing invited emails from library
        console.log('[openEditModal] Opening library:', lib.name);
        console.log('[openEditModal] Raw invited_emails from lib:', lib.invited_emails);

        const existingEmails = lib.invited_emails ? lib.invited_emails.split(',').map(e => e.trim()).filter(e => e.length > 0) : [];
        console.log('[openEditModal] Parsed existingEmails:', existingEmails);

        setInvitedEmails(existingEmails);
        setOriginalInvitedEmails(existingEmails);
        setNewEmailInput('');
        const existingBuyers = (lib.buyer_emails || '').split(',').map(e => e.trim().toLowerCase()).filter(Boolean);
        setBuyerEmails(existingBuyers);
        setOriginalBuyerEmails(existingBuyers);
        setNewBuyerInput('');
        setBuyerPrice(Number(lib.buyer_price) > 0 ? Number(lib.buyer_price) : 5);
        setBuyerBundles(Array.isArray(lib.buyer_bundles) ? lib.buyer_bundles.map(b => ({ photo_count: Number(b.photo_count), price: Number(b.price) })) : []);
        setBuyerAlbumPrice(Number(lib.buyer_album_price) > 0 ? Number(lib.buyer_album_price) : '');
        setAlbumPrice(Number(lib.album_price) > 0 ? Number(lib.album_price) : '');
        setDeletedPricingIds([]);
        setIsManaged(true);
    };

    const openCreateModal = () => {
        setEditingId(null);
        setModalData({
            name: '',
            slug: '',
            description: '',
            is_private: false,
            published: true,
            google_drive_folder_id: '',
            cover_image_url: '',
            price: 5.00,
            commercial_included: false,
        });
        setFilesData([]);
        setUploadCounts(null);
        setFileStatus(new Map());
        setUploadProgress(0);
        setInvitedEmails(['media@thelostandunfounds.com']);
        setOriginalInvitedEmails(['media@thelostandunfounds.com']);
        setNewEmailInput('');
        setBuyerEmails([]);
        setOriginalBuyerEmails([]);
        setNewBuyerInput('');
        setBuyerPrice(5);
        setBuyerBundles([]);
        setBuyerAlbumPrice('');
        setAlbumPrice('');
        setIsManaged(true);
    };

    // Called from the photo grid's "Save" action once edits are made — stages
    // the edited photos so the next successfully-created gallery adopts them.
    const openCreateModalForPhotos = (photoIds: string[]) => {
        setPendingGalleryPhotoIds(photoIds);
        openCreateModal();
    };

    // Cover photo (owner, 2026-11-02: "it's not saving"). The gallery-photos
    // bucket takes JPEG/PNG/WebP up to 2 MB, and a phone photo is usually a
    // bigger HEIC, so it is shrunk to a 1600px JPEG here first. Uploads go to
    // covers/<user id>/ — the only place the storage policy
    // gallery_covers_owner_insert lets a signed-in person write.
    const [coverUploading, setCoverUploading] = useState(false);
    const handleCoverImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !user) return;
        setCoverUploading(true);
        try {
            const blob = await shrinkToJpeg(file, 1600);
            const filePath = `covers/${user.id}/${Date.now()}.jpg`;
            const { error: uploadError } = await supabase.storage
                .from('gallery-photos')
                .upload(filePath, blob, { contentType: 'image/jpeg' });
            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('gallery-photos')
                .getPublicUrl(filePath);

            setModalData(prev => ({ ...prev, cover_image_url: publicUrl }));
            success('Cover photo ready — tap SAVE GALLERY to keep it');
        } catch (err: any) {
            console.error('[cover] upload failed', err);
            error(`Cover photo did not upload: ${err?.message || 'unknown error'}`);
        } finally {
            setCoverUploading(false);
        }
    };

    const checkAssetHealth = async () => {
        setVerifying(true);
        try {
            info('Starting asset health check...');

            // First, try to get an actual photo from the database — use most recent image to avoid stale IDs
            const { data: photos, error: photoError } = await supabase
                .from('photos')
                .select('google_drive_file_id')
                .not('google_drive_file_id', 'is', null)
                .like('mime_type', 'image/%')
                .order('created_at', { ascending: false })
                .limit(1);

            if (photoError) {
                console.error('Error fetching photos for health check:', photoError);
                setHealthStatus('issues');
                error('Failed to query photos database');
                return;
            }

            if (!photos || photos.length === 0) {
                setHealthStatus('unknown');
                info('No photos in database yet. Upload photos to test asset delivery.');
                return;
            }

            const testFileId = photos[0].google_drive_file_id;
            const res = await fetch(`/api/gallery/stream?fileId=${testFileId}`);

            if (res.ok) {
                setHealthStatus('healthy');
                success('Gallery Asset System is HEALTHY. Public access verified.');
            } else {
                setHealthStatus('issues');
                if (res.status === 403) {
                    error('CRITICAL: Google Drive Permissions Error (403). Files must be set to "Anyone with link can view"');
                } else if (res.status === 404) {
                    error('File not found (404). The Google Drive file may have been deleted or moved.');
                } else {
                    error(`System Error: ${res.status}. Check server logs for details.`);
                }
            }

        } catch (err) {
            console.error('Health check error:', err);
            setHealthStatus('issues');
            error('Failed to verify asset health. Check network connection and API endpoints.');
        } finally {
            // Show result for 3 seconds then reset the button
            setTimeout(() => setVerifying(false), 3000);
        }
    };

    const triggerResend = async (orderId: string, email: string) => {
        if (!confirm(`Resend download email to ${email}?`)) return;

        try {
            const res = await fetch('/api/gallery/resend-order', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orderId, email })
            });

            if (!res.ok) throw new Error('Failed to send');

            success('Email sent successfully');
        } catch (err) {
            error('Failed to send email');
        }
    };

    return (
        <div className="flex flex-col h-full overflow-x-hidden">

            {/* Create/Edit Gallery Modal — fullscreen ExpandableScreen, same pattern as Affiliates/ProductCostManagement */}
            <ExpandableScreen isOpen={isManaged} onOpenChange={setIsManaged}>
                <ExpandableScreenContent className="overflow-y-auto">
                    <div className="w-full max-w-5xl mx-auto px-6 sm:px-10 pt-20 pb-16">
                            <div className="flex items-start justify-between mb-6 pr-10 gap-2">
                                <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2 min-w-0">
                                    {editingId ? <PencilIcon className="w-5 h-5 text-white/60" /> : <PlusIcon className="w-5 h-5 text-white/60" />}
                                    {editingId ? 'Edit Gallery Settings' : 'Create New Gallery'}
                                </h2>
                                {(() => {
                                    const isPublished = modalData.published ?? true;
                                    return (
                                        <button
                                            type="button"
                                            onClick={() => setModalData({ ...modalData, published: !isPublished })}
                                            className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 transition-colors"
                                            title={isPublished ? 'Published — visible on site. Click to switch to draft.' : 'Draft — hidden from production. Click to publish.'}
                                            aria-pressed={isPublished}
                                            aria-label={isPublished ? 'Switch to draft' : 'Publish gallery'}
                                        >
                                            {isPublished ? (
                                                <Eye className="w-4 h-4 text-white" />
                                            ) : (
                                                <EyeOff className="w-4 h-4 text-white/30" />
                                            )}
                                            <span
                                                className={`text-[10px] font-black uppercase tracking-[0.2em] ${isPublished ? 'text-white' : 'text-white/30'}`}
                                            >
                                                {isPublished ? 'Published' : 'Draft'}
                                            </span>
                                        </button>
                                    );
                                })()}
                            </div>

                            <form data-tour="gallery-form" onSubmit={handleSaveGallery} className="space-y-6">
                                {/* Gallery Name & Slug */}
                                <div data-tour="gallery-form-name" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-1">Gallery Name</label>
                                        <input
                                            type="text"
                                            required
                                            value={modalData.name}
                                            onChange={(e) => {
                                                const name = e.target.value;
                                                if (!editingId) {
                                                    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
                                                    setModalData({ ...modalData, name, slug });
                                                } else {
                                                    setModalData({ ...modalData, name });
                                                }
                                            }}
                                            className="w-full bg-white/5 p-2 text-white placeholder-white/20 focus:outline-none rounded-none"
                                            placeholder="e.g. Summer 2025 Collection"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-1">URL Slug</label>
                                        <div className="flex items-center gap-2 bg-white/5 p-2 opacity-50">
                                            <span className="text-white/40 text-xs">/gallery/</span>
                                            <input
                                                type="text"
                                                required
                                                value={modalData.slug}
                                                onChange={(e) => setModalData({ ...modalData, slug: e.target.value })}
                                                className="bg-transparent text-white w-full focus:outline-none font-mono text-sm rounded-none"
                                                placeholder="summer-2025"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Private decides who can buy, so it sits right above Pricing. */}
                                <label data-tour="gallery-form-private" className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={!!modalData.is_private}
                                        onChange={(e) => setModalData({ ...modalData, is_private: e.target.checked })}
                                        className="w-4 h-4 bg-white/5 border-none rounded-none focus:ring-0 text-white"
                                    />
                                    <span className="text-white text-xs uppercase tracking-widest">Private Gallery</span>
                                    <span className="text-[10px] text-white/30 normal-case">Invite only</span>
                                </label>

                                {/* PRICING — every price on the gallery, in one place (owner, 2026-11-02).
                                    One row = label left, box right, never wrapping on a phone. */}
                                <div data-tour="gallery-form-pricing" className="space-y-3">
                                    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-white/40">Pricing</label>
                                    {/* A private gallery sells only to its à la carte list (album
                                        owners are free, everyone else is refused), so it shows those
                                        prices instead. */}
                                    {!modalData.is_private && (
                                    <div className="bg-white/5 p-3 space-y-3">
                                        <PriceRow tour="gallery-form-single-price" label="Per photo" value={singlePriceValue} onChange={v => setSinglePrice(v === '' ? 0 : v)} />
                                        <BundleRows
                                            tour="gallery-form-bundles"
                                            bundles={pricingOptions.map((o, i) => ({ ...o, i })).filter(o => o.photo_count !== 1)}
                                            onChange={(i, patch) => setPricingOptions(pricingOptions.map((o, j) => j === i ? { ...o, ...patch } : o))}
                                            onRemove={(i) => {
                                                const o = pricingOptions[i];
                                                if (o?.id) setDeletedPricingIds([...deletedPricingIds, o.id]);
                                                setPricingOptions(pricingOptions.filter((_, j) => j !== i));
                                            }}
                                            onAdd={() => setPricingOptions([...pricingOptions, { id: '', name: '', photo_count: 3, price: 10 }])}
                                        />
                                        <PriceRow tour="gallery-form-album-price" label="Whole album" value={albumPrice} onChange={setAlbumPrice} placeholder="Off" />
                                    </div>
                                    )}
                                    {modalData.is_private && (
                                    <div className="bg-white/5 p-3 space-y-3" data-tour="gallery-form-pricing-a-la-carte">
                                        <p className={PRICE_GROUP}>À la carte buyers</p>
                                        <p className="text-[10px] text-white/30 -mt-2">Album owners get everything free.</p>
                                        <PriceRow tour="gallery-a-la-carte-price" label="Per photo" value={buyerPrice} onChange={v => setBuyerPrice(v === '' ? 0 : v)} />
                                        <BundleRows
                                            tour="gallery-a-la-carte-bundles"
                                            bundles={buyerBundles.map((b, i) => ({ ...b, i }))}
                                            onChange={(i, patch) => setBuyerBundles(buyerBundles.map((x, j) => j === i ? { ...x, ...patch } : x))}
                                            onRemove={(i) => setBuyerBundles(buyerBundles.filter((_, j) => j !== i))}
                                            onAdd={() => setBuyerBundles([...buyerBundles, { photo_count: 4, price: 10 }])}
                                        />
                                        <PriceRow
                                            tour="gallery-a-la-carte-album"
                                            label="Whole album"
                                            value={buyerAlbumPrice}
                                            onChange={setBuyerAlbumPrice}
                                            placeholder="Off"
                                        />
                                    </div>
                                    )}
                                    <p className="text-[10px] text-white/30">Per photo 0 = free. Whole album blank = not offered.</p>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-1">Description</label>
                                    <textarea
                                        value={modalData.description}
                                        onChange={(e) => setModalData({ ...modalData, description: e.target.value })}
                                        className="w-full bg-white/5 p-2 text-white placeholder-white/20 focus:outline-none min-h-[80px] rounded-none"
                                        placeholder="Brief description of the gallery contents..."
                                    />
                                </div>

                                {/* Client Invitations */}
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-2">Invited Clients</label>

                                    {/* Existing email chips */}
                                    {invitedEmails.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {invitedEmails.map((email, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`flex items-center gap-1 px-2 py-1 text-xs font-mono ${
                                                        email === 'media@thelostandunfounds.com'
                                                            ? 'bg-white/10 text-white font-bold'
                                                            : originalInvitedEmails.includes(email)
                                                                ? 'bg-white/10 text-white/60'
                                                                : 'bg-emerald-500/20 text-emerald-400'
                                                    }`}
                                                >
                                                    <span>{email}</span>
                                                    {!originalInvitedEmails.includes(email) && email !== 'media@thelostandunfounds.com' && (
                                                        <span className="text-[9px] uppercase ml-1 opacity-60">new</span>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => setInvitedEmails(invitedEmails.filter((_, i) => i !== idx))}
                                                        className="ml-1 hover:text-red-400 transition-colors"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Add new email input */}
                                    <div className="flex gap-2">
                                        <input
                                            type="email"
                                            value={newEmailInput}
                                            onChange={(e) => setNewEmailInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ',') {
                                                    e.preventDefault();
                                                    const email = newEmailInput.trim().replace(',', '').toLowerCase();
                                                    if (email && email.includes('@') && !invitedEmails.includes(email)) {
                                                        setInvitedEmails([...invitedEmails, email]);
                                                        setNewEmailInput('');
                                                    }
                                                }
                                            }}
                                            className="flex-1 bg-white/5 p-2 text-white placeholder-white/20 focus:outline-none rounded-none font-mono text-xs"
                                            placeholder="client@example.com"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const email = newEmailInput.trim().toLowerCase();
                                                if (email && email.includes('@') && !invitedEmails.includes(email)) {
                                                    setInvitedEmails([...invitedEmails, email]);
                                                    setNewEmailInput('');
                                                }
                                            }}
                                            className="px-4 py-2 bg-white/10 text-white text-xs uppercase font-bold hover:bg-white/20 transition-colors"
                                        >
                                            + Add
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-white/30 mt-2">
                                        Press Enter or click Add. New clients will receive an access link when you save.
                                    </p>
                                    {invitedEmails.length > 0 && editingId && (
                                        <button
                                            type="button"
                                            onClick={handleResendAllInvites}
                                            className="mt-2 text-[10px] uppercase font-bold text-white/40 hover:text-white/80 tracking-widest transition-colors"
                                        >
                                            ↩ Resend Access Emails to All
                                        </button>
                                    )}
                                </div>

                                {/* À la carte — same album, buy per photo. Private galleries only:
                                    a public gallery already sells to anyone. */}
                                {modalData.is_private && (
                                <div data-tour="gallery-form-a-la-carte">
                                    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-1">À La Carte</label>
                                    <p className="text-[10px] text-white/30 mb-3">
                                        They see the whole album behind the watermark and buy the photos they want. Album owners above get everything free.
                                    </p>
                                    {buyerEmails.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {buyerEmails.map((email, idx) => (
                                                <div
                                                    key={email}
                                                    className={`flex items-center gap-1 px-2 py-1 text-xs font-mono ${originalBuyerEmails.includes(email) ? 'bg-white/10 text-white/60' : 'bg-emerald-500/20 text-emerald-400'}`}
                                                >
                                                    <span>{email}</span>
                                                    {!originalBuyerEmails.includes(email) && <span className="text-[9px] uppercase ml-1 opacity-60">new</span>}
                                                    <button
                                                        type="button"
                                                        onClick={() => setBuyerEmails(buyerEmails.filter((_, i) => i !== idx))}
                                                        className="ml-1 hover:text-red-400 transition-colors"
                                                        aria-label={`Remove ${email}`}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="flex gap-2">
                                        <input
                                            type="email"
                                            value={newBuyerInput}
                                            onChange={(e) => setNewBuyerInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ',') {
                                                    e.preventDefault();
                                                    const email = newBuyerInput.trim().replace(',', '').toLowerCase();
                                                    if (email && email.includes('@') && !buyerEmails.includes(email)) {
                                                        setBuyerEmails([...buyerEmails, email]);
                                                        setNewBuyerInput('');
                                                    }
                                                }
                                            }}
                                            className="flex-1 bg-white/5 p-2 text-white placeholder-white/20 focus:outline-none rounded-none font-mono text-xs"
                                            placeholder="buyer@example.com"
                                            data-tour="gallery-a-la-carte-email"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const email = newBuyerInput.trim().toLowerCase();
                                                if (email && email.includes('@') && !buyerEmails.includes(email)) {
                                                    setBuyerEmails([...buyerEmails, email]);
                                                    setNewBuyerInput('');
                                                }
                                            }}
                                            className="px-4 py-2 bg-white/10 text-white text-xs uppercase font-bold hover:bg-white/20 transition-colors"
                                        >
                                            + Add
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-white/30 mt-2">
                                        New buyers get their own invite link when you save. Their prices are under Pricing above.
                                    </p>
                                </div>
                                )}

                                <div>
                                    <label className="block text-xs uppercase text-white/40 mb-2">Cover Image</label>
                                    <div className="flex items-start gap-4">
                                        {modalData.cover_image_url && (
                                            <div className="w-24 h-16 bg-zinc-800 rounded-none overflow-hidden flex-shrink-0">
                                                <img src={modalData.cover_image_url} alt="Cover" className="w-full h-full object-cover" />
                                            </div>
                                        )}
                                        <div className="flex-1 space-y-2">
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={modalData.cover_image_url}
                                                    onChange={(e) => setModalData({ ...modalData, cover_image_url: e.target.value })}
                                                    className="flex-1 bg-white/5 p-2 text-xs text-white rounded-none"
                                                    placeholder="https://... (or upload below)"
                                                />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={modalData.commercial_included}
                                                    onChange={(e) => setModalData({ ...modalData, commercial_included: e.target.checked })}
                                                    className="w-4 h-4 bg-white/5 border-none rounded-none focus:ring-0 text-emerald-500"
                                                />
                                                <span className="text-emerald-400 text-xs uppercase tracking-widest font-bold">Commercial Rights Included</span>
                                            </div>
                                            <p className="text-[10px] text-white/30 mt-1 pl-6">
                                                If checked, displays "Eligible for Commercial Use" and assumes license is granted.
                                            </p>
                                            {coverUploading && <p className="text-[10px] text-white/50 uppercase tracking-widest">Uploading cover…</p>}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                disabled={coverUploading}
                                                onChange={handleCoverImageUpload}
                                                className="block w-full text-xs text-white/60
                                               file:mr-4 file:py-2 file:px-4
                                               file:rounded-none file:border-0
                                               file:text-xs file:font-semibold
                                               file:bg-white/10 file:text-white
                                               hover:file:bg-white/20"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <hr className="border-none" />

                                {editingId && (
                                    <div data-tour="gallery-form-photos">
                                        <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-2">
                                            In This Gallery ({galleryPhotos.length})
                                        </label>
                                        {galleryPhotos.length === 0 ? (
                                            <p className="text-[11px] text-white/40">No photos yet.</p>
                                        ) : (
                                            // The three newest, and a tile that opens the rest in their
                                            // own card. Tiles are held square by padding, not
                                            // aspect-ratio: iOS Safari squeezed aspect-ratio tiles.
                                            <div className="grid grid-cols-4 gap-1">
                                                {galleryPhotos.slice(0, 3).map(p => (
                                                    <GalleryThumb
                                                        key={p.id}
                                                        photo={p}
                                                        isCover={isCoverPhoto(p)}
                                                        onTap={() => { setShowAllPhotos(true); setSelectedPhotoIds(new Set([p.id])); }}
                                                    />
                                                ))}
                                                {galleryPhotos.length > 3 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowAllPhotos(true)}
                                                        className="relative block w-full h-0 pb-[100%] bg-white/5 hover:bg-white/10 transition-colors"
                                                        data-tour="gallery-form-photos-more"
                                                    >
                                                        <span className="absolute inset-0 flex flex-col items-center justify-center text-white">
                                                            <span className="text-sm font-black">+{galleryPhotos.length - 3}</span>
                                                            <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/60">More</span>
                                                        </span>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div data-tour="gallery-form-source">
                                    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-3">Asset Source</label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                                        <button
                                            type="button"
                                            onClick={() => setUploadMode('drive')}
                                            className={`flex items-center justify-start gap-2 px-4 py-3 text-left text-xs uppercase font-bold border-none transition-all ${uploadMode === 'drive' ? 'bg-white text-black' : 'bg-transparent text-white/40 hover:text-white hover:bg-white/10'}`}
                                        >
                                            <CloudIcon className="w-4 h-4 shrink-0" /> <span className="whitespace-nowrap">{editingId ? 'Update Drive Folder' : 'Link Google Drive'}</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setUploadMode('upload')}
                                            className={`flex items-center justify-start gap-2 px-4 py-3 text-left text-xs uppercase font-bold border-none transition-all ${uploadMode === 'upload' ? 'bg-white text-black' : 'bg-transparent text-white/40 hover:text-white hover:bg-white/10'}`}
                                        >
                                            <ArrowUpTrayIcon className="w-4 h-4 shrink-0" /> <span className="whitespace-nowrap">{editingId ? 'Add More Photos' : 'Direct Upload'}</span>
                                        </button>
                                    </div>

                                    {uploadMode === 'drive' ? (
                                        <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                                            <label className="block text-xs uppercase text-white/40 mb-1">Google Drive Folder ID or Link</label>
                                            <input
                                                type="text"
                                                value={modalData.google_drive_folder_id}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    // Detect if it's a Drive URL and extract ID
                                                    const match = val.match(/\/folders\/([a-zA-Z0-9-_]+)/) || val.match(/id=([a-zA-Z0-9-_]+)/);
                                                    const extractedId = match ? match[1] : val;
                                                    setModalData({ ...modalData, google_drive_folder_id: extractedId });
                                                }}
                                                className="w-full bg-white/5 p-2 text-white placeholder-white/20 focus:outline-none font-mono text-xs"
                                                placeholder="Paste folder link or ID here..."
                                                required={uploadMode === 'drive' && !filesData.length} // Only required if not uploading files in mixed mode (simplified logic)
                                            />
                                            <p className="text-[10px] text-white/40 mt-1 flex items-center gap-1">
                                                <ExclamationCircleIcon className="w-3 h-3" />
                                                Folder must be set to "Anyone with link can view"
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="animate-in fade-in slide-in-from-top-2 duration-200 space-y-3">
                                            <div className="bg-white/5 p-6 text-left hover:bg-white/10 transition-colors relative">
                                                <input
                                                    type="file"
                                                    multiple
                                                    accept="image/*,video/*"
                                                    onChange={handleFileSelect}
                                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                                />
                                                <div className="pointer-events-none">
                                                    <ArrowUpTrayIcon className="w-6 h-6 text-white/40 mb-2" />
                                                    <p className="text-white text-xs uppercase font-bold">Click or Drag Photos & Videos Here</p>
                                                    <p className="text-white/40 text-[10px] mt-1">{filesData.length} files selected</p>
                                                    <p className="text-white/30 text-[10px] mt-1 normal-case">Videos up to 1GB (about 2 minutes). Post longer ones to YouTube.</p>
                                                </div>
                                            </div>

                                            {filesData.length > 0 && (
                                                <div className="max-h-[220px] overflow-y-auto bg-black/40 p-2 grid grid-cols-2 gap-2 custom-scrollbar">
                                                    {filesData.map((item, i) => (
                                                        <div key={i} className="flex items-center gap-2 overflow-hidden text-xs text-white bg-white/5 p-2 rounded-none relative group">
                                                            <div className="w-10 h-10 flex-shrink-0 bg-black/50 overflow-hidden relative">
                                                                {item.file.type.startsWith('video/') && (
                                                                    <div className="absolute inset-0 flex items-center justify-center z-10">
                                                                        <div className="w-4 h-4 rounded-none bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                                                            <div className="w-0 h-0 border-l-[4px] border-l-white border-y-[3px] border-y-transparent ml-0.5" />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                <img src={item.previewUrl} className="w-full h-full object-cover" alt="" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="truncate text-[10px]">{item.file.name}</p>
                                                                {(() => {
                                                                    const st = fileStatus.get(item.file);
                                                                    const size = `${Math.round(item.file.size / 1024)} KB`;
                                                                    if (!st) return <p className="text-[9px] text-white/40 uppercase">{size}</p>;
                                                                    if (st.state === 'done') return <p className="text-[9px] uppercase font-bold text-green-400">✓ Live</p>;
                                                                    if (st.state === 'failed') return <p className="text-[9px] uppercase font-bold text-red-400" title={st.error}>Failed</p>;
                                                                    if (st.state === 'sending') return <p className="text-[9px] uppercase text-white">Sending {Math.round(st.progress * 100)}%</p>;
                                                                    return <p className="text-[9px] uppercase text-white/40">Waiting · {size}</p>;
                                                                })()}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeFile(i)}
                                                                className="absolute top-1 right-1 p-1 bg-black/50 text-white/40 hover:text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                                            >
                                                                <XMarkIcon className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {uploadCounts && (
                                                <div>
                                                    <div className="flex justify-between text-[10px] text-white/60 mb-1">
                                                        <span>
                                                            {saving ? 'Uploading — switching apps pauses it; it picks up when you come back' : 'Upload finished'}
                                                            {` · ${uploadCounts.done} of ${uploadCounts.total} done`}
                                                            {uploadCounts.failed > 0 ? ` · ${uploadCounts.failed} failed` : ''}
                                                        </span>
                                                        <span>{Math.round(uploadProgress)}%</span>
                                                    </div>
                                                    <div className="h-1 bg-white/10 w-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-white transition-all duration-300 ease-out"
                                                            style={{ width: `${uploadProgress}%` }}
                                                        />
                                                    </div>
                                                    {uploadCounts.lastError && uploadCounts.failed > 0 && (
                                                        <p className="mt-1 text-[10px] text-red-400">{uploadCounts.lastError}</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="pt-4 flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setIsManaged(false)}
                                        className="flex-1 px-4 py-3 text-left text-white/60 hover:text-white hover:bg-white/5 transition uppercase text-xs font-bold"
                                    >
                                        Cancel
                                    </button>
                                    <button data-tour="gallery-form-save"
                                        type="submit"
                                        disabled={saving || coverUploading}
                                        className="flex-1 px-4 py-3 text-left bg-white text-black hover:bg-white/90 transition uppercase text-xs font-bold disabled:opacity-60"
                                    >
                                        {saving
                                            ? (uploadCounts ? `Uploading ${uploadCounts.done + uploadCounts.failed}/${uploadCounts.total}` : 'Saving…')
                                            : (editingId ? 'Save Gallery' : 'Create Gallery')}
                                    </button>
                                </div>
                            </form>
                    </div>
                </ExpandableScreenContent>
            </ExpandableScreen>

            {/* Every photo in the gallery being edited, opened from +N MORE. */}
            <ExpandableScreen isOpen={showAllPhotos && isManaged} onOpenChange={setShowAllPhotos}>
                <ExpandableScreenContent className="overflow-y-auto">
                    <div className="w-full max-w-5xl mx-auto px-6 sm:px-10 pt-20 pb-16" data-tour="gallery-all-photos">
                        <h2 className="text-lg sm:text-xl font-bold text-white uppercase mb-1 pr-10">{modalData.name || 'Gallery'}</h2>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40 mb-4">
                            {galleryPhotos.length} {galleryPhotos.length === 1 ? 'photo' : 'photos'} · newest first · tap to select
                        </p>
                        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-1" data-tour="gallery-all-photos-grid">
                            {galleryPhotos.map(p => (
                                <GalleryThumb
                                    key={p.id}
                                    photo={p}
                                    size={400}
                                    selected={selectedPhotoIds.has(p.id)}
                                    isCover={isCoverPhoto(p)}
                                    onTap={() => togglePhoto(p.id)}
                                />
                            ))}
                        </div>
                    </div>
                    {/* What to do with the selection. Sticky inside the scroller,
                        so it stays on screen without a fixed layer over the grid. */}
                    {selectedPhotoIds.size > 0 && (
                        <div className="sticky bottom-0 z-10 bg-black/90 backdrop-blur-md pb-[env(safe-area-inset-bottom)]" data-tour="gallery-photo-actions">
                            <div className="w-full max-w-5xl mx-auto px-6 sm:px-10 py-3 space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white">
                                        {selectedPhotoIds.size} selected
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedPhotoIds(new Set())}
                                        className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white/60 hover:text-white"
                                    >
                                        Clear
                                    </button>
                                </div>
                                <div className="flex gap-2">
                                {selectedPhotoIds.size === 1 && (
                                    <button
                                        type="button"
                                        onClick={() => setViewingPhoto(galleryPhotos.find(p => selectedPhotoIds.has(p.id)) || null)}
                                        className="flex-1 px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-white bg-white/10 hover:bg-white/20"
                                        data-tour="gallery-photo-view"
                                    >
                                        View
                                    </button>
                                )}
                                {(() => {
                                    const only = selectedPhotoIds.size === 1 ? galleryPhotos.find(p => selectedPhotoIds.has(p.id)) : undefined;
                                    const canCover = !!only && !only.mime_type?.startsWith('video/');
                                    return (
                                        <button
                                            type="button"
                                            onClick={makeSelectedCover}
                                            disabled={!canCover || !!photoAction || (!!only && isCoverPhoto(only))}
                                            title={selectedPhotoIds.size > 1 ? 'Select one photo to make it the cover' : undefined}
                                            className="flex-1 px-3 py-3 text-[10px] font-bold uppercase tracking-wider bg-white text-black hover:bg-white/90 disabled:opacity-40 flex items-center justify-center gap-1 whitespace-nowrap"
                                            data-tour="gallery-photo-cover"
                                        >
                                            <StarIcon className="w-3 h-3" />
                                            {photoAction === 'cover' ? 'Saving…' : only && isCoverPhoto(only) ? 'Cover' : 'Make Cover'}
                                        </button>
                                    );
                                })()}
                                <button
                                    type="button"
                                    onClick={deleteSelectedPhotos}
                                    disabled={!!photoAction}
                                    className="flex-1 px-3 py-3 text-[10px] font-bold uppercase tracking-wider bg-red-500/20 text-red-400 hover:bg-red-500/30 disabled:opacity-40 flex items-center justify-center gap-1"
                                    data-tour="gallery-photo-delete"
                                >
                                    <TrashIcon className="w-3 h-3" />
                                    {photoAction === 'delete' ? 'Deleting…' : 'Delete'}
                                </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* One photo, full size, on black — in place of opening the bare image file. */}
                    {viewingPhoto && (
                        <div className="fixed inset-0 z-20 bg-black flex items-center justify-center" onClick={() => setViewingPhoto(null)}>
                            <button
                                type="button"
                                onClick={() => setViewingPhoto(null)}
                                className="absolute top-6 right-6 p-2 hover:bg-white/10"
                                aria-label="Close photo"
                            >
                                <XMarkIcon className="w-6 h-6 text-white" />
                            </button>
                            {viewingPhoto.mime_type?.startsWith('video/') ? (
                                <video src={photoSrc(viewingPhoto, 1600)} controls playsInline className="max-w-full max-h-full" onClick={e => e.stopPropagation()} />
                            ) : (
                                <img src={photoSrc(viewingPhoto, 1600)} alt={viewingPhoto.title} className="max-w-full max-h-full object-contain" />
                            )}
                        </div>
                    )}
                </ExpandableScreenContent>
            </ExpandableScreen>

            {/* Compact header — title + actions */}
            <div className={`shrink-0 flex items-center gap-3 pb-3 ${hideTitle ? 'justify-start' : 'justify-between'}`}>
                {!hideTitle && <div className="flex items-center gap-2">
                    <div className="bg-white p-1.5">
                        <PhotoIcon className="w-4 h-4 text-black" />
                    </div>
                    <h2 className="text-sm font-black text-white uppercase tracking-widest">
                        {isPhotographerView ? 'My Galleries' : 'Gallery Management'}
                    </h2>
                    {!isPhotographerView && pendingAppsCount > 0 && (
                        <span className="flex items-center justify-center bg-red-500 text-white text-[8px] font-black h-4 w-4 rounded-full animate-pulse">
                            {pendingAppsCount}
                        </span>
                    )}
                </div>}
                <div className="flex flex-wrap items-center gap-2">
                    {!isPhotographerView && (
                        // A router Link, not <a href>: a full page load would end a
                        // walkthrough that is stepping through to the live console.
                        <Link
                            to="/admin/live"
                            className="p-2 bg-white/5 hover:bg-white/10 text-green-400 text-[10px] uppercase tracking-wider font-bold flex items-center gap-1"
                            title="Live events — camera uploads and review inbox"
                            data-tour="gallery-live-link"
                        >
                            <Radio className="w-3 h-3" />
                            <span>Live</span>
                        </Link>
                    )}
                    <button
                        onClick={() => setIsHalftoneOpen(true)}
                        className="p-2 bg-white/5 hover:bg-white/10 text-white/60 text-[10px] uppercase tracking-wider font-bold flex items-center gap-1"
                        title="Screen generator — halftone film positives for screen printing"
                        aria-label="Screen generator"
                        data-tour="gallery-screens-button"
                    >
                        <span className="text-xs leading-none" aria-hidden="true">▲</span>
                        <span>Screens</span>
                    </button>
                    {!isPhotographerView && (
                        <button data-tour="gallery-invite"
                            onClick={() => setIsInviteModalOpen(true)}
                            className="p-2 bg-white/5 hover:bg-white/10 text-white/60 text-[10px] uppercase tracking-wider font-bold flex items-center gap-1"
                        >
                            <EnvelopeIcon className="w-3 h-3" />
                            <span className="hidden sm:inline">Invite</span>
                        </button>
                    )}
                    <button data-tour="gallery-health"
                        onClick={checkAssetHealth}
                        disabled={verifying}
                        className="p-2 bg-white/5 hover:bg-white/10 text-white/60 text-[10px] uppercase tracking-wider font-bold flex items-center gap-1"
                    >
                        {verifying ? <LoadingSpinner size="sm" /> : <ChartBarIcon className="w-3 h-3" />}
                        <span className="hidden sm:inline">{verifying ? 'Checking' : 'Health'}</span>
                    </button>
                    <button data-tour="gallery-new"
                        onClick={openCreateModal}
                        className="px-3 py-2 bg-white text-black text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 hover:bg-white/90"
                    >
                        <PlusIcon className="w-3 h-3" />
                        New
                    </button>
                </div>
            </div>

            {/* Inline metrics — admin only, single compact row */}
            {!isPhotographerView && (
                <div className="shrink-0 flex flex-wrap items-center gap-x-6 gap-y-1 py-2 px-3 mb-2 bg-white/5">
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-white/40 uppercase tracking-[0.2em]">Orders</span>
                        <span className="text-sm font-bold font-mono text-white"><AnimatedNumber value={stats?.totalOrders || 0} /></span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-white/40 uppercase tracking-[0.2em]">Gallery</span>
                        <span className="text-sm font-bold font-mono text-green-400">$<AnimatedNumber value={stats?.totalRevenue || 0} decimals={2} /></span>
                    </div>
                    {(stats?.bookingRevenue ?? 0) > 0 && (
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-white/40 uppercase tracking-[0.2em]">Bookings</span>
                            <span className="text-sm font-bold font-mono text-blue-300">$<AnimatedNumber value={stats?.bookingRevenue || 0} decimals={2} /></span>
                        </div>
                    )}
                </div>
            )}

            {/* Health status banners */}
            {healthStatus === 'issues' && (
                <div className="shrink-0 flex items-center gap-2 py-2 px-3 mb-2 bg-red-500/10 text-red-400 text-xs">
                    <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
                    <span>Asset delivery issue. Verify Drive files are "Anyone with link can view", then run Health check.</span>
                </div>
            )}
            {healthStatus === 'healthy' && (
                <div className="shrink-0 flex items-center gap-2 py-2 px-3 mb-2 bg-green-500/10 text-green-400 text-xs">
                    <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                    <span>Asset system healthy — delivery verified.</span>
                </div>
            )}

            {/* Tab bar — admin only */}
            {!isPhotographerView && (
                <div className="shrink-0 flex overflow-x-auto border-b border-white/10">
                    {[
                        { id: 'galleries' as const, label: 'Active Galleries', icon: null, badge: 0 },
                        { id: 'applications' as const, label: 'Applications', icon: null, badge: pendingAppsCount },
                        { id: 'photos' as const, label: 'All Photos', icon: <PhotoIcon className="w-3 h-3" />, badge: 0 },
                        { id: 'tags' as const, label: 'Tags', icon: <TagIcon className="w-3 h-3" />, badge: 0 },
                        { id: 'downloads' as const, label: 'Downloads', icon: <ArrowDownTrayIcon className="w-3 h-3" />, badge: 0 },
                    ].map(tab => (
                        <button data-tour={`gallery-tab-${tab.id}`}
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as typeof activeTab)}
                            className={`px-3 py-2.5 text-[10px] sm:text-[11px] uppercase font-bold tracking-widest transition-colors relative flex items-center gap-1.5 whitespace-nowrap ${activeTab === tab.id ? 'text-white' : 'text-white/40 hover:text-white'}`}
                        >
                            {tab.icon}
                            {tab.label}
                            {tab.badge > 0 && (
                                <span className="bg-red-500 text-white text-[8px] px-1 font-black">{tab.badge}</span>
                            )}
                            {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />}
                        </button>
                    ))}
                </div>
            )}

            {/* Tab content — fills remaining height, only this scrolls */}
            <div className="flex-1 min-h-0 overflow-y-auto pt-4 custom-scrollbar">
                {activeTab === 'downloads' ? (
                    <AdminDownloadsPanel />
                ) : activeTab === 'tags' ? (
                    <AdminTagsPanel />
                ) : activeTab === 'photos' ? (
                    <AdminPhotosBrowse onRequestCreateGallery={openCreateModalForPhotos} />
                ) : activeTab === 'galleries' || isPhotographerView ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Active Galleries */}
                        <div className="bg-white/[0.02] p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-xs font-bold text-white uppercase tracking-wide">
                                    Active Galleries ({libraries.length})
                                </h3>
                                <button onClick={loadGalleryStats} className="text-white/40 hover:text-white">
                                    {loading ? <LoadingSpinner size="sm" /> : <ArrowPathIcon className="w-3 h-3" />}
                                </button>
                            </div>

                            {/* Every library in one run. Renaming was offered per gallery
                                only, so a single job became eight — and the endpoint always
                                supported the whole set (it filters by slug only when given). */}
                            <button
                                onClick={() => setRenameFor(renameFor === '__all__' ? null : '__all__')}
                                className={`mb-3 flex items-center gap-2 px-3 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors ${renameFor === '__all__' ? 'bg-white text-black' : 'bg-white/10 text-white hover:bg-white hover:text-black'}`}
                            >
                                <TagIcon className="w-3.5 h-3.5" />
                                Rename all galleries
                            </button>
                            {renameFor === '__all__' && (
                                <div className="mb-3">
                                    <RetrogradeRenameTool libraryName="ALL GALLERIES" />
                                </div>
                            )}

                            {loading && libraries.length === 0 ? (
                                <div className="text-white/40 text-sm py-4">Loading galleries...</div>
                            ) : (
                                <div className="space-y-1">
                                    {libraries.length === 0 ? (
                                        <div className="text-white/40 text-sm py-4">No galleries found.</div>
                                    ) : (
                                        libraries.map((lib) => {
                                            const isLastNight = lib.name.toUpperCase() === 'LAST NIGHT';
                                            const showCountdown = isLastNight && !countdownExpired && shouldShowCountdown(lib.name, targetTime8PM);

                                            const handleCountdownComplete = async () => {
                                                setCountdownExpired(true);
                                                try {
                                                    info('Gallery opening! Syncing photos...');
                                                    const syncRes = await fetch('/api/gallery/sync');
                                                    if (syncRes.ok) {
                                                        success('LAST NIGHT gallery is now live!');
                                                        loadGalleryStats();
                                                    }
                                                } catch (err) {
                                                    console.error('Sync error:', err);
                                                }
                                            };

                                            return (
                                                <div data-tour="gallery-row" key={lib.id} className="relative group">
                                                    {showCountdown && (
                                                        <GalleryCountdownOverlay
                                                            coverImageUrl={lib.cover_image_url}
                                                            targetTime={targetTime8PM}
                                                            galleryName={lib.name}
                                                            onCountdownComplete={handleCountdownComplete}
                                                        />
                                                    )}
                                                    {/* The whole card opens the gallery; the icons do their own
                                                        thing (their row stops the tap). On a phone the icons sit on
                                                        their own line so the name isn't squeezed out. */}
                                                    <div
                                                        className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 p-3 bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                                                        onClick={() => openEditModal(lib)}
                                                        onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openEditModal(lib); } }}
                                                        role="button"
                                                        tabIndex={0}
                                                        aria-label={`Open ${lib.name}`}
                                                        data-tour="gallery-row-open"
                                                    >
                                                        <div className="flex-1 min-w-0 md:mr-4">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                {(lib.published ?? true) ? (
                                                                    <Eye className="w-3 h-3 text-white flex-shrink-0" />
                                                                ) : (
                                                                    <EyeOff className="w-3 h-3 text-white/30 flex-shrink-0" />
                                                                )}
                                                                {lib.is_private ? (
                                                                    <LockClosedIcon className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                                                                ) : (
                                                                    <GlobeAltIcon className="w-3 h-3 text-green-400 flex-shrink-0" />
                                                                )}
                                                                <h4 className={`font-bold text-sm truncate ${(lib.published ?? true) ? 'text-white' : 'text-white/40'}`}>{lib.name}</h4>
                                                                {!(lib.published ?? true) && (
                                                                    <span className="text-[9px] font-black uppercase tracking-[0.2em] text-white/30">Draft</span>
                                                                )}
                                                                {lib.live_mode && (
                                                                    <span className="text-[9px] font-black uppercase tracking-[0.2em] text-green-400">● Live</span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2 text-xs text-white/40 font-mono">
                                                                <span className="truncate">/{lib.slug}</span>
                                                                <span>•</span>
                                                                <span className="flex-shrink-0">{lib.photo_count || 0} photos</span>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-wrap gap-1 -ml-2 md:ml-0" onClick={(e) => e.stopPropagation()}>
                                                            {!isPhotographerView && (
                                                                <a
                                                                    href={`/admin/live?slug=${encodeURIComponent(lib.slug)}`}
                                                                    className={`p-2 hover:bg-white/20 ${lib.live_mode ? 'text-green-400' : 'text-white/60 hover:text-white'}`}
                                                                    title="Live event — camera uploads and review inbox"
                                                                    aria-label={`Live event for ${lib.name}`}
                                                                >
                                                                    <Radio className="w-4 h-4" />
                                                                </a>
                                                            )}
                                                            {!isPhotographerView && lib.google_drive_folder_id && (
                                                                <a
                                                                    href={`https://drive.google.com/drive/folders/${encodeURIComponent(lib.google_drive_folder_id)}`}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="p-2 hover:bg-white/20 text-white/60 hover:text-white"
                                                                    title="Open this gallery's Google Drive folder — upload there from the Drive app, then SYNC"
                                                                    aria-label={`Google Drive folder for ${lib.name}`}
                                                                    data-tour="gallery-drive-folder"
                                                                >
                                                                    <CloudIcon className="w-4 h-4" />
                                                                </a>
                                                            )}
                                                            {!isPhotographerView && lib.google_drive_folder_id && (
                                                                <button
                                                                    onClick={() => handleSyncFromDrive(lib)}
                                                                    disabled={syncingSlug !== null}
                                                                    className="p-2 hover:bg-white/20 text-white/60 hover:text-white disabled:opacity-40"
                                                                    title="Sync from Drive — pull in photos added to the Drive folder"
                                                                    aria-label={`Sync ${lib.name} from Google Drive`}
                                                                    data-tour="gallery-sync-drive"
                                                                >
                                                                    <ArrowPathIcon className={`w-4 h-4 ${syncingSlug === lib.slug ? 'animate-spin' : ''}`} />
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => setQrLibrary({ slug: lib.slug, name: lib.name })}
                                                                className="p-2 hover:bg-white/20 text-white/60 hover:text-white"
                                                                title="QR code and wallpaper"
                                                                aria-label={`QR code for ${lib.name}`}
                                                                data-tour="gallery-qr-button"
                                                            >
                                                                <QrCodeIcon className="w-4 h-4" />
                                                            </button>
                                                            <a
                                                                href={`/gallery/${lib.slug}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="p-2 hover:bg-white/20 text-white/60 hover:text-white"
                                                                title="View Gallery"
                                                            >
                                                                <ArrowLeftIcon className="w-4 h-4 rotate-180" />
                                                            </a>
                                                            <button
                                                                onClick={() => setRenameFor(renameFor === lib.slug ? null : lib.slug)}
                                                                className={`p-2 hover:bg-white/20 ${renameFor === lib.slug ? 'text-white bg-white/10' : 'text-white/60 hover:text-white'}`}
                                                                title="Retrograde rename"
                                                            >
                                                                <TagIcon className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteGallery(lib.id, lib.name)}
                                                                className="p-2 hover:bg-red-500/20 text-white/60 hover:text-red-400"
                                                                title="Delete Gallery"
                                                            >
                                                                <TrashIcon className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {renameFor === lib.slug && (
                                                        <div className="mt-2">
                                                            <RetrogradeRenameTool librarySlug={lib.slug} libraryName={lib.name} />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Recent Sales — admin only */}
                        {!isPhotographerView && (
                            <div className="bg-white/[0.02] p-4">
                                <h3 className="text-xs font-bold text-white uppercase mb-3 tracking-wide">
                                    Recent Sales
                                </h3>
                                {loading && !stats ? (
                                    <div className="text-white/40 text-sm py-4">Loading orders...</div>
                                ) : (
                                    <div className="space-y-1">
                                        {stats?.recentOrders.length === 0 ? (
                                            <div className="text-white/40 text-sm py-4">No orders found.</div>
                                        ) : (
                                            stats?.recentOrders.map((order) => (
                                                <div key={order.id} className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 transition-colors">
                                                    <div>
                                                        <div className="text-white font-mono text-xs">{order.email}</div>
                                                        <div className="text-white/40 text-[10px]">
                                                            {(order.total_amount_cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })} · {new Date(order.created_at).toLocaleDateString()}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => triggerResend(order.id, order.email)}
                                                        className="text-[10px] uppercase font-bold text-white/60 hover:text-white px-2 py-1 bg-white/5 hover:bg-white/10 transition"
                                                    >
                                                        Resend
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    /* Applications tab */
                    <div className="animate-in fade-in duration-300">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xs font-bold text-white uppercase tracking-wide">
                                Pending Applications ({pendingAppsCount})
                            </h3>
                            <button onClick={loadApplications} className="text-white/40 hover:text-white">
                                {appsLoading ? <LoadingSpinner size="sm" /> : <ArrowPathIcon className="w-3 h-3" />}
                            </button>
                        </div>
                        {appsLoading && applications.length === 0 ? (
                            <div className="text-white/40 text-sm py-8 text-center italic">Loading applications...</div>
                        ) : (
                            <div className="space-y-3">
                                {applications.length === 0 ? (
                                    <div className="text-white/40 text-sm py-8 text-center italic">No applications received yet.</div>
                                ) : (
                                    applications.map((app) => (
                                        <div key={app.id} className="bg-white/5 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-white font-bold text-sm">{app.name}</span>
                                                    <span className={`text-[9px] px-1.5 py-0.5 uppercase font-bold ${app.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' : app.status === 'approved' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                                                        {app.status}
                                                    </span>
                                                </div>
                                                <div className="text-white/60 text-xs font-mono">{app.email}</div>
                                                {app.portfolio_link && (
                                                    <a
                                                        href={app.portfolio_link.startsWith('http') ? app.portfolio_link : `https://${app.portfolio_link}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-blue-400 text-[10px] underline"
                                                    >
                                                        View Portfolio →
                                                    </a>
                                                )}
                                            </div>
                                            {app.status === 'pending' && (
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleApplicationStatus(app.id, 'approved')}
                                                        className="px-3 py-1.5 bg-green-500/20 hover:bg-green-500 text-green-500 hover:text-black text-[10px] uppercase font-bold transition-all"
                                                    >
                                                        Approve
                                                    </button>
                                                    <button
                                                        onClick={() => handleApplicationStatus(app.id, 'rejected')}
                                                        className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 text-red-500 text-[10px] uppercase font-bold transition-all"
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>


            {isHalftoneOpen && (
                <Suspense fallback={<span data-tour-loading hidden />}>
                    <HalftoneModal
                        onClose={() => setIsHalftoneOpen(false)}
                        ownerId={isPhotographerView && user && !isAdminUser(user) ? user.id : undefined}
                    />
                </Suspense>
            )}

            {qrLibrary && (
                <QrWallpaperModal slug={qrLibrary.slug} name={qrLibrary.name} onClose={() => setQrLibrary(null)} />
            )}

            {/* Photographer Invite Modal */}
            {
                isInviteModalOpen && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                        <div className="bg-[#0A0A0A] w-full max-w-md p-6 relative">
                            <button
                                onClick={() => setIsInviteModalOpen(false)}
                                className="absolute top-4 right-4 text-white/40 hover:text-white"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                                <EnvelopeIcon className="w-5 h-5 text-white/60" />
                                Invite Photographer
                            </h2>
                            <p className="text-zinc-400 text-sm mb-6">Send an invitation link to a photographer to set up their own gallery connection.</p>

                            <form onSubmit={async (e) => {
                                e.preventDefault();
                                setInviteLoading(true);
                                try {
                                    const res = await fetch('/api/admin/invite-photographer', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify(inviteData)
                                    });
                                    const data = await res.json();
                                    if (res.ok) {
                                        success('Invitation sent! Link: ' + data.inviteUrl);
                                        setIsInviteModalOpen(false);
                                        setInviteData({ email: '', name: '' });
                                    } else {
                                        error(data.error || 'Failed to send');
                                    }
                                } catch (err) {
                                    error('Failed to invite');
                                } finally {
                                    setInviteLoading(false);
                                }
                            }} className="space-y-4">
                                <div>
                                    <label className="block text-xs uppercase text-white/40 mb-1">Photographer Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={inviteData.name}
                                        onChange={e => setInviteData({ ...inviteData, name: e.target.value })}
                                        className="w-full bg-white/5 p-2 text-white placeholder-white/20 focus:outline-none focus:border-white/40"
                                        placeholder="Jane Doe"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs uppercase text-white/40 mb-1">Email Address</label>
                                    <input
                                        type="email"
                                        required
                                        value={inviteData.email}
                                        onChange={e => setInviteData({ ...inviteData, email: e.target.value })}
                                        className="w-full bg-white/5 p-2 text-white placeholder-white/20 focus:outline-none focus:border-white/40"
                                        placeholder="jane@example.com"
                                    />
                                </div>
                                <div className="pt-4 flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={inviteLoading}
                                        className="px-6 py-2 bg-white text-black font-bold uppercase tracking-wider hover:bg-white/90 disabled:opacity-50"
                                    >
                                        {inviteLoading ? 'Sending...' : 'Send Invitation'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }
        </div>
    );
}
